// frontend/src/pages/cargo/PolicyScheduleStep.js
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import {
    FileCheck, Download, CheckCircle2, Loader2, Eye,
    Printer, AlertCircle, X, ExternalLink,
} from 'lucide-react';

export default function PolicyScheduleStep({ acceptance, certificate, onComplete }) {
    const navigate = useNavigate();

    const [scheduleData, setScheduleData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState(null);

    const certId = certificate?.id || acceptance?.certificate_id;

    useEffect(() => {
        if (!certId) {
            setError('No certificate found. Please generate a certificate first.');
            setLoading(false);
            return;
        }
        fetchScheduleData();
        // eslint-disable-next-line
    }, [certId]);

    const fetchScheduleData = async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await api.get(`/cargo/certificates/${certId}/policy-schedule-data`);
            setScheduleData(data);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load policy schedule data.');
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        if (!certId) return;
        setDownloading(true);
        try {
            const token = localStorage.getItem('token');
            const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5038/api/v1';
            const res = await fetch(`${apiBase}/cargo/certificates/${certId}/policy-schedule-pdf`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!res.ok) throw new Error('Download failed');

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Mayfair-Policy-Schedule-${(scheduleData?.policy_number || 'draft').replace(/[:/]/g, '-')}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            alert('Could not download policy schedule: ' + err.message);
        } finally {
            setDownloading(false);
        }
    };

    const handleComplete = () => {
        if (onComplete) onComplete({ schedule: scheduleData });
        navigate('/cargo/certificates');
    };

    // ---------- Loading ----------
    if (loading) {
        return (
            <div className="max-w-3xl mx-auto text-center py-20 animate-fadeIn">
                <div className="relative inline-flex items-center justify-center w-24 h-24 mb-6">
                    <div className="absolute inset-0 rounded-full bg-brand-500/20 animate-ping" />
                    <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-brand-700 to-brand-600 flex items-center justify-center shadow-2xl">
                        <Loader2 className="w-10 h-10 text-white animate-spin" />
                    </div>
                </div>
                <h3 className="text-2xl font-extrabold text-gray-800 dark:text-white mb-2">
                    Preparing Policy Schedule
                </h3>
                <p className="text-gray-500 dark:text-slate-400">
                    Gathering your certificate details...
                </p>
            </div>
        );
    }

    // ---------- Error ----------
    if (error) {
        return (
            <div className="max-w-2xl mx-auto text-center py-16 animate-fadeIn">
                <div className="bg-red-50 dark:bg-red-900/20 rounded-3xl p-8 border border-red-100 dark:border-red-900/30 shadow-xl">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
                        <AlertCircle className="w-10 h-10 text-red-600 dark:text-red-400" />
                    </div>
                    <h3 className="text-xl font-bold text-red-800 dark:text-red-400 mb-2">
                        Could Not Load Policy Schedule
                    </h3>
                    <p className="text-red-600 dark:text-red-300 mb-6">{error}</p>
                    <button
                        onClick={fetchScheduleData}
                        className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors shadow-lg"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    // ---------- Success ----------
    return (
        <div className="max-w-4xl mx-auto animate-fadeIn space-y-6">

            {/* Hero */}
            <div className="text-center space-y-4">
                <div className="relative inline-flex items-center justify-center w-24 h-24">
                    <div className="absolute inset-0 rounded-full bg-green-500/20 animate-ping" />
                    <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-2xl">
                        <FileCheck className="w-10 h-10 text-white" />
                    </div>
                </div>
                <h2 className="text-4xl font-extrabold text-gray-800 dark:text-white tracking-tight">
                    Policy Schedule Ready
                </h2>
                <p className="text-gray-500 dark:text-slate-400 max-w-md mx-auto">
                    Your marine cargo policy schedule has been generated from certificate{' '}
                    <strong className="text-brand-600 dark:text-green-400">
                        {scheduleData?.policy_number}
                    </strong>
                </p>
            </div>

            {/* Summary card */}
            <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl border border-gray-100 dark:border-slate-700 overflow-hidden">
                <div className="p-5 text-white bg-gradient-to-r from-brand-700 via-brand-600 to-brand-500">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20">
                                <FileCheck className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-widest text-white/70">
                                    Policy Schedule
                                </p>
                                <p className="text-xl font-extrabold tracking-tight">
                                    {scheduleData?.policy_number}
                                </p>
                            </div>
                        </div>
                        <div className="hidden sm:block text-right">
                            <p className="text-xs uppercase tracking-wider text-white/70">
                                Sum Insured
                            </p>
                            <p className="text-xl font-extrabold">
                                {scheduleData?.currency} {Number(scheduleData?.sum_insured || 0).toLocaleString()}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Tile label="Insured" value={scheduleData?.insured_name} />
                    <Tile label="Vessel" value={scheduleData?.vessel_name} />
                    <Tile label="From" value={scheduleData?.from_location} />
                    <Tile label="To" value={scheduleData?.to_location} />
                    <Tile label="Period Start" value={formatDate(scheduleData?.coverage_start)} />
                    <Tile label="Period End" value={formatDate(scheduleData?.coverage_end)} />
                    <Tile label="Created By" value={scheduleData?.created_by_name} />
                    <Tile label="Approved By" value={scheduleData?.approved_by_name} />
                </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
                <button
                    onClick={handleDownload}
                    disabled={downloading}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-brand-700 to-brand-600 hover:from-brand-600 hover:to-brand-500 text-white font-bold py-3.5 rounded-2xl shadow-lg hover:shadow-xl transition-all disabled:opacity-60"
                >
                    {downloading ? (
                        <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Downloading...
                        </>
                    ) : (
                        <>
                            <Download className="w-5 h-5" />
                            Download Schedule PDF
                        </>
                    )}
                </button>

                <button
                    onClick={handleComplete}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-white dark:bg-slate-800 border-2 border-brand-600 text-brand-600 dark:text-green-400 hover:bg-brand-600 hover:text-white font-bold py-3.5 rounded-2xl transition-all"
                >
                    <CheckCircle2 className="w-5 h-5" />
                    Finish &amp; View Certificates
                </button>
            </div>

            <p className="text-xs text-center text-gray-400 dark:text-slate-500">
                The policy schedule is generated automatically from the certificate and covers all standard clauses.
            </p>
        </div>
    );
}

function Tile({ label, value }) {
    return (
        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3 border border-gray-100 dark:border-slate-700">
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-slate-500">
                {label}
            </p>
            <p className="text-sm font-bold text-gray-800 dark:text-white truncate mt-0.5" title={value || '�'}>
                {value || '�'}
            </p>
        </div>
    );
}

function formatDate(v) {
    if (!v) return '�';
    try {
        return new Date(v).toLocaleDateString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric',
        });
    } catch {
        return String(v);
    }
}// JavaScript source code
